import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Plus } from 'lucide-react';
import { useCertificationCohorts } from '@/lib/hooks/use-certification-cohorts';

export function CreateCohortDialog() {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const { createCohort, isCreating } = useCertificationCohorts();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || name.trim().length < 3) return;

    await createCohort({
      name: name.trim(),
      description: description.trim() || null,
    });

    setName('');
    setDescription('');
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm">
          <Plus className="h-4 w-4 mr-1" />
          Create Cohort
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Create a Certification Cohort</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="cohort-name">Cohort Name *</Label>
            <Input
              id="cohort-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., West Coast AI Architects"
              maxLength={100}
              required
              minLength={3}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="cohort-description">Description (optional)</Label>
            <Textarea
              id="cohort-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What's the focus of this cohort? When do you plan to meet?"
              className="resize-none h-24"
              maxLength={1000}
            />
          </div>
          <p className="text-xs text-muted-foreground">
            You'll be the organizer. The cohort can have up to 10 members total (including you).
          </p>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isCreating || name.trim().length < 3}>
              {isCreating ? 'Creating...' : 'Create Cohort'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
